import { useQuery } from "convex/react";
import { useEffect, useMemo, useState } from "react";
import {
  Code2,
  Eye,
  LayoutGrid,
  List,
  Mail,
  ShieldCheck,
  UserRound,
  Users,
} from "lucide-react";
import { api } from "../../convex/_generated/api";

type ViewMode = "focus" | "list" | "grid";

type EmailPreview = {
  id: string;
  category: string;
  audience: "artist" | "owner";
  name: string;
  subject: string;
  from: string;
  to: string;
  replyTo?: string;
  html: string;
  text: string;
  source?: string;
  sourceLabel?: string;
};

type PreviewResponse = {
  success: boolean;
  generatedAt: string;
  businessName: string;
  businessEmail: string;
  sendingEnabled: boolean;
  sendingGate: string;
  templates: EmailPreview[];
};

const VIEW_MODES: Array<{ id: ViewMode; label: string; icon: typeof Eye }> = [
  { id: "focus", label: "Focus", icon: Eye },
  { id: "list", label: "List", icon: List },
  { id: "grid", label: "Grid", icon: LayoutGrid },
];

function formatDateTime(value?: string): string {
  if (!value) return "Not loaded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not loaded";
  return date.toLocaleString("en-AU", { timeZone: "Australia/Brisbane" });
}

function audienceLabel(audience: EmailPreview["audience"]): string {
  return audience === "owner" ? "To you (BJ)" : "To artist";
}

function audienceIcon(audience: EmailPreview["audience"]) {
  return audience === "owner" ? Users : UserRound;
}

function TemplateChips({ template }: { template: EmailPreview }) {
  const AudienceIcon = audienceIcon(template.audience);
  return (
    <div className="email-preview-chips">
      <span>
        <AudienceIcon aria-hidden="true" />
        {audienceLabel(template.audience)}
      </span>
      <span>{template.category}</span>
      <span>{template.sourceLabel || "Johnsonboibeats"}</span>
    </div>
  );
}

function EmailMetaGrid({ template }: { template: EmailPreview }) {
  return (
    <div className="email-preview-meta-grid">
      <div>
        <span>Recipient</span>
        <strong>{audienceLabel(template.audience)}</strong>
      </div>
      <div>
        <span>From</span>
        <strong>{template.from}</strong>
      </div>
      <div>
        <span>To</span>
        <strong>{template.to}</strong>
      </div>
      <div>
        <span>Reply-To</span>
        <strong>{template.replyTo || "bj@johnsonboibeats.com"}</strong>
      </div>
    </div>
  );
}

function EmailPreviewFrame({
  template,
  compact = false,
}: {
  template: EmailPreview;
  compact?: boolean;
}) {
  return (
    <div className={`email-preview-frame ${compact ? "compact" : ""}`}>
      <iframe
        title={`${template.name} email preview`}
        sandbox=""
        srcDoc={template.html}
      />
    </div>
  );
}

export default function EmailPreviews() {
  const previewData = useQuery(api.portalEmailPreviews.listPortalEmailPreviews);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState("All");
  const [viewMode, setViewMode] = useState<ViewMode>("focus");

  const data = previewData as PreviewResponse | null | undefined;
  const loading = previewData === undefined;
  const error = previewData instanceof Error ? previewData.message : null;

  useEffect(() => {
    if (data?.templates) {
      setSelectedId((current) =>
        current && data.templates.some((template) => template.id === current)
          ? current
          : data.templates[0]?.id ?? null,
      );
    }
  }, [data]);

  const categories = useMemo(
    () => ["All", ...new Set((data?.templates ?? []).map((template) => template.category))],
    [data?.templates],
  );

  const filteredTemplates = useMemo(() => {
    const templates = data?.templates ?? [];
    return activeCategory === "All"
      ? templates
      : templates.filter((template) => template.category === activeCategory);
  }, [activeCategory, data?.templates]);

  const selectedTemplate = useMemo(() => {
    const templates = data?.templates ?? [];
    return templates.find((template) => template.id === selectedId) ?? templates[0] ?? null;
  }, [data?.templates, selectedId]);

  useEffect(() => {
    if (!filteredTemplates.length) return;
    if (!selectedTemplate || !filteredTemplates.some((template) => template.id === selectedTemplate.id)) {
      setSelectedId(filteredTemplates[0].id);
    }
  }, [filteredTemplates, selectedTemplate]);

  return (
    <div className="email-previews-page">
      <section className="email-previews-heading">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>Email viewer</h1>
          <p>See the Johnsonboibeats transactional emails.</p>
          <small>
            {data?.businessName || "Johnsonboibeats"} · Updated {formatDateTime(data?.generatedAt)}
          </small>
        </div>
        <div className="email-previews-heading-actions">
          <div className="email-view-mode" aria-label="Email preview view mode">
            {VIEW_MODES.map((mode) => {
              const Icon = mode.icon;
              return (
                <button
                  key={mode.id}
                  type="button"
                  className={viewMode === mode.id ? "active" : ""}
                  onClick={() => setViewMode(mode.id)}
                >
                  <Icon aria-hidden="true" />
                  {mode.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="email-disabled-banner" role="status">
        <ShieldCheck aria-hidden="true" />
        <div>
          <strong>{data?.sendingEnabled ? "Delivery gate is enabled" : "Email sending is disabled"}</strong>
          <span>
            {data?.sendingEnabled
              ? `The preview query is read-only. Delivery requires the approved gate: ${data.sendingGate}.`
              : "Changing old project statuses will not send email. The delivery gate requires two explicit production flags."}
          </span>
        </div>
      </section>

      <div className="email-preview-categories" aria-label="Email categories">
        {categories.map((category) => (
          <button
            key={category}
            type="button"
            className={activeCategory === category ? "active" : ""}
            onClick={() => setActiveCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      {loading && !data ? (
        <section className="content-panel email-preview-state" role="status">Loading email previews…</section>
      ) : error ? (
        <section className="content-panel email-preview-state" role="alert">
          <Mail aria-hidden="true" />
          <strong>Email previews unavailable</strong>
          <span>{error}</span>
        </section>
      ) : viewMode === "list" ? (
        <section className="content-panel email-preview-list">
          <div className="panel-heading compact">
            <div>
              <p className="eyebrow">{filteredTemplates.length} templates</p>
              <h2>All email definitions</h2>
            </div>
          </div>
          {filteredTemplates.map((template) => (
            <div className="email-preview-list-row" key={template.id}>
              <div>
                <strong>{template.name}</strong>
                <span>{template.subject}</span>
                <small>To: {template.to}</small>
              </div>
              <TemplateChips template={template} />
              <button
                type="button"
                onClick={() => {
                  setSelectedId(template.id);
                  setViewMode("focus");
                }}
              >
                <Eye aria-hidden="true" /> View
              </button>
            </div>
          ))}
        </section>
      ) : viewMode === "grid" ? (
        <div className="email-preview-grid">
          {filteredTemplates.map((template) => (
            <section className="content-panel email-preview-grid-card" key={template.id}>
              <div className="email-preview-card-heading">
                <div>
                  <p className="eyebrow">{template.category}</p>
                  <h2>{template.name}</h2>
                  <span>{template.subject}</span>
                </div>
              </div>
              <TemplateChips template={template} />
              <EmailPreviewFrame compact template={template} />
            </section>
          ))}
        </div>
      ) : (
        <div className="email-preview-focus">
          <section className="content-panel email-preview-template-list">
            <div className="panel-heading compact">
              <div>
                <p className="eyebrow">{filteredTemplates.length} shown</p>
                <h2>Templates</h2>
              </div>
            </div>
            <div>
              {filteredTemplates.map((template) => (
                <button
                  type="button"
                  key={template.id}
                  className={selectedTemplate?.id === template.id ? "selected" : ""}
                  onClick={() => setSelectedId(template.id)}
                >
                  <span>
                    <strong>{template.name}</strong>
                    <small>{template.subject}</small>
                  </span>
                  <Eye aria-hidden="true" />
                </button>
              ))}
            </div>
          </section>

          {selectedTemplate ? (
            <div className="email-preview-focus-detail">
              <section className="content-panel email-preview-detail-heading">
                <div>
                  <p className="eyebrow">{selectedTemplate.category} · {audienceLabel(selectedTemplate.audience)}</p>
                  <h2>{selectedTemplate.name}</h2>
                  <p>{selectedTemplate.subject}</p>
                </div>
                <div className="email-preview-source"><Code2 aria-hidden="true" /> Johnsonboibeats email</div>
                <TemplateChips template={selectedTemplate} />
                <EmailMetaGrid template={selectedTemplate} />
              </section>
              <section className="content-panel email-preview-rendered">
                <div className="panel-heading compact">
                  <div>
                    <p className="eyebrow">Full email</p>
                    <h2>Rendered email</h2>
                  </div>
                </div>
                <EmailPreviewFrame template={selectedTemplate} />
              </section>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
